import { REGULATIONS, ORGANIZER } from "@/data/regulations";
import { MapPin, Calendar, Mail, Phone, Building2, FileText } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";

export default function Regulamin() {
  useDocumentTitle("Regulamin", "Regulamin zapisów, sprzedaży i uczestnictwa w mamy!kongres 2027.");
  return (
    <div>
      {/* HERO */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-8 pb-14">
        <div className="text-xs uppercase tracking-[0.3em] text-[#E6007A] mb-6">Regulamin</div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6">
          Regulamin zapisów, sprzedaży <br />i uczestnictwa w{" "}
          <span className="italic text-[#E6007A]">wydarzeniu</span>.
        </h1>
        <p className="text-lg text-[#5C534D] max-w-3xl">
          Zapoznaj się z zasadami zakupu biletów oraz uczestnictwa w Mamy Kongres.
        </p>
      </section>

      {/* ORGANIZER CARD */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
        <div className="rounded-3xl bg-[#F4EFE6] p-8 md:p-10 grid md:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#5C534D] mb-3">
              <Building2 className="w-4 h-4 text-[#E6007A]" /> Organizator
            </div>
            <div className="font-display text-2xl mb-3">{ORGANIZER.name}</div>
            <div className="text-[#5C534D] leading-relaxed">
              {ORGANIZER.addressLines.map((l, i) => <div key={i}>{l}</div>)}
              <div className="mt-3">NIP: {ORGANIZER.nip}</div>
              <div>KRS: {ORGANIZER.krs}</div>
            </div>
            <div className="mt-4 space-y-1">
              <a href={`mailto:${ORGANIZER.email}`} className="flex items-center gap-2 text-[#2B2623] hover:text-[#E6007A] transition-colors">
                <Mail className="w-4 h-4 text-[#E6007A]" /> {ORGANIZER.email}
              </a>
              <div className="flex items-center gap-2 text-[#5C534D]">
                <Phone className="w-4 h-4 text-[#E6007A]" /> {ORGANIZER.phone}
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#5C534D] mb-3">
              <FileText className="w-4 h-4 text-[#E6007A]" /> Wydarzenie
            </div>
            <div className="font-display text-2xl mb-3">Mamy Kongres</div>
            <div className="space-y-2 text-[#5C534D]">
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-[#E6007A] mt-1" />
                <span>Data: {ORGANIZER.eventDate}</span>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#E6007A] mt-1" />
                <span>Miejsce: {ORGANIZER.eventVenue}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TABLE OF CONTENTS */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
        <div className="rounded-3xl bg-white border border-[#E8E1D9] p-8 md:p-10">
          <div className="text-xs uppercase tracking-[0.3em] text-[#E6007A] mb-5">Spis treści</div>
          <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2 list-none">
            {REGULATIONS.map((s) => (
              <li key={s.n}>
                <a
                  href={`#sec-${s.n}`}
                  className="flex gap-3 py-1 text-[#2B2623] hover:text-[#E6007A] transition-colors"
                >
                  <span className="font-display text-[#E6007A] w-10 shrink-0">§ {s.n}</span>
                  <span>{s.title}</span>
                </a>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24">
        <div className="space-y-14">
          {REGULATIONS.map((s) => (
            <article
              key={s.n}
              id={`sec-${s.n}`}
              className="scroll-mt-32"
              data-testid={`reg-sec-${s.n}`}
            >
              <div className="flex items-baseline gap-4 mb-6">
                <span className="font-display text-4xl md:text-5xl text-[#E6007A] leading-none">
                  § {s.n}
                </span>
                <h2 className="font-display text-2xl md:text-3xl leading-tight text-[#2B2623]">
                  {s.title}
                </h2>
              </div>
              <ol className="space-y-4 text-[#3d3733] leading-relaxed list-none">
                {s.items.reduce((acc, item, i) => {
                  // Determine display number: paragraphs count, checkboxes/pure lists do not
                  const prevCount = acc.reduce((n, it) => n + (it.props['data-counts'] ? 1 : 0), 0);
                  const isCounted = Boolean(item.p);
                  const number = isCounted ? prevCount + 1 : null;
                  acc.push(
                    <li key={i} data-counts={isCounted ? "1" : undefined} className="flex gap-3">
                      {number !== null ? (
                        <span className="font-display text-[#E6007A] shrink-0 w-8 text-right">
                          {number}.
                        </span>
                      ) : (
                        <span className="w-8 shrink-0" />
                      )}
                      <div className="flex-1 space-y-3">
                        {item.p && <p>{item.p}</p>}
                        {item.list && (
                          <ol className="mt-2 space-y-2 list-none">
                            {item.list.map((li, j) => (
                              <li key={j} className="flex gap-3">
                                <span className="font-display text-[#E6007A] shrink-0 w-8 text-right">
                                  {number ?? "·"}.{j + 1}
                                </span>
                                <span>{li}</span>
                              </li>
                            ))}
                          </ol>
                        )}
                        {item.checkbox && (
                          <div className="rounded-2xl bg-[#F4EFE6] p-5 flex gap-4">
                            <span className="w-5 h-5 border-2 border-[#E6007A] rounded shrink-0 mt-0.5" />
                            <div>
                              <div className="font-semibold text-[#2B2623] mb-1">
                                {item.checkbox.label}
                              </div>
                              <div className="text-sm text-[#5C534D]">{item.checkbox.text}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                  return acc;
                }, [])}
              </ol>
            </article>
          ))}
        </div>

        <div className="mt-16 rounded-3xl bg-[#F4EFE6] p-8 text-center">
          <p className="text-[#5C534D]">
            Pytania do regulaminu?{" "}
            <a href={`mailto:${ORGANIZER.email}`} className="text-[#E6007A] font-semibold link-underline">
              {ORGANIZER.email}
            </a>
          </p>
        </div>

        <div className="mt-6 text-center text-xs uppercase tracking-[0.3em] text-[#5C534D]">
          Data ostatniej aktualizacji: 15.09.2026
        </div>
      </section>
    </div>
  );
}
